import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import Loader from 'components/common/Loader';
import {
  getDataEnrichmentId,
  getEnrichmentType,
} from 'selectors/enrichmentWizard';
import { downloadEnrichment, enrichmentSummary } from 'services/api';
import Icon from 'components/common/Icon';
import {
  DownloadReportContainer,
  ErrorBox,
  FileHeader,
  FileInfo,
  MessageText,
  StatBox,
  StatLabel,
  StatsContainer,
  StatValue,
} from './styled';
import { ENRICHMENT_TYPE } from 'components/DataEnrichment/helpers';
import Link from 'components/common/Link';
import { useGet } from 'hooks';
import Accordion from 'components/common/Accordion';
import { SecondaryText } from 'components/common/FileUpload';
import { Button } from 'components/common';

const SummaryContainer = styled.div`
  width: 90%;
`;

const LinkContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const SummarySection = styled.div`
  margin-top: 16px;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 16px;
`;

const LoaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  flex: 1;
`;

const NewEmailPip = styled.div`
  background-color: #86b174;
  border-radius: 12px;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  position: absolute;
  bottom: -12px;
  padding: 2px 8px;
  left: 50%;
  transform: translateX(-50%);
  text-wrap: nowrap;
  text-align: center;
`;

const SummaryStep = () => {
  const dataEnrichmentId = useSelector(getDataEnrichmentId);
  const type = useSelector(getEnrichmentType);

  const [{ res, loading, error }, fetchSummary] = useGet({
    url: enrichmentSummary(dataEnrichmentId),
    onMount: false,
  });

  const [
    { res: downloadRes, loading: downloadLoading, error: downloadError },
    fetchDownload,
  ] = useGet({
    url: downloadEnrichment(dataEnrichmentId),
    onMount: false,
  });

  useEffect(() => {
    if (dataEnrichmentId) {
      fetchSummary();
    }
  }, [fetchSummary, dataEnrichmentId]);

  useEffect(() => {
    if (downloadRes?.data) {
      const blob = new Blob([downloadRes.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `${type}-enrichment-summary-${res?.listName || 'list'}.csv`
      );
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadRes, dataEnrichmentId]);

  const handleDownload = () => {
    if (!downloadLoading) {
      fetchDownload();
    }
  };

  if (loading) {
    return (
      <LoaderContainer>
        <MessageText>Loading summary...</MessageText>
        <Loader size={20} />
      </LoaderContainer>
    );
  }

  if (error) {
    return (
      <MessageText>
        <ErrorBox>
          <Icon icon='error' size='sm' color='red' />
          <div>
            Something went wrong. If the issue persists contact your Customer
            Success Manager.
          </div>
        </ErrorBox>
      </MessageText>
    );
  }

  if (!res) {
    return null;
  }

  return (
    <SummaryContainer>
      <SummarySection>
        <Accordion title='File Upload Summary:' isCollapsedByDefault={true}>
          <FileHeader>
            <FileInfo>
              <Icon icon='fileExcel' size='sm' color='green' /> {res?.filename}
              {res?.sheetName && <span>({res?.sheetName})</span>}
            </FileInfo>
          </FileHeader>
          <StatsContainer>
            <StatBox>
              <StatValue>{res.totalRows}</StatValue>
              <StatLabel>Total Rows</StatLabel>
            </StatBox>
            <StatBox>
              <StatValue>{res.emptyRows}</StatValue>
              <StatLabel>Empty Rows</StatLabel>
            </StatBox>
            <StatBox>
              <StatValue>{res.validRows}</StatValue>
              <StatLabel>Valid Rows</StatLabel>
            </StatBox>
            <StatBox>
              <StatValue>{res.invalidRows}</StatValue>
              <StatLabel>Invalid Rows</StatLabel>
            </StatBox>
          </StatsContainer>
        </Accordion>
        <Accordion title='File Enrichment Summary:'>
          {res.status === 2 ? (
            <SecondaryText>
              <p>
                Unfortunately, the data enrichment for the file {res.filename}{' '}
                has not completed successfully.
              </p>
              <p>
                Please try uploading and enriching the file again. If you
                continue to experience issues, don't hesitate to reach out to
                your Customer Success representative for assistance.
              </p>
            </SecondaryText>
          ) : (
            <>
              <StatsContainer>
                <StatBox>
                  <StatValue>{res.totalUploaded}</StatValue>
                  <StatLabel>Rows Uploaded</StatLabel>
                </StatBox>
                <StatBox>
                  <StatValue>{res.researchersMatched}</StatValue>
                  <StatLabel>Rows Matched</StatLabel>
                </StatBox>
                <StatBox>
                  <StatValue>{res.researchersNotFound}</StatValue>
                  <StatLabel>Rows Not Matched</StatLabel>
                </StatBox>
                <StatBox>
                  <StatValue>{res.totalResults}</StatValue>
                  <StatLabel>
                    {res.type === 1 ? 'Organisations' : 'Contacts'} Found
                  </StatLabel>
                  {res.newEmailsFound > 0 && (
                    <NewEmailPip>
                      {res.newEmailsFound} New Email
                      {res.newEmailsFound > 1 ? 's' : ''}
                    </NewEmailPip>
                  )}
                </StatBox>
              </StatsContainer>
              <DownloadReportContainer>
                <div>
                  Generate a report of matches, mismatches and row validity
                </div>
                <Button
                  type='secondary'
                  onClick={handleDownload}
                  disabled={downloadLoading}
                >
                  <Icon icon='download' size='sm' color='inherit' />
                  <span style={{ marginLeft: 8 }}>Download Summary</span>
                </Button>
                {downloadError && (
                  <MessageText style={{ color: 'red', marginTop: '8px' }}>
                    Error downloading file. Please try again.
                  </MessageText>
                )}
              </DownloadReportContainer>
            </>
          )}
        </Accordion>
      </SummarySection>
      {res?.listId && (
        <LinkContainer>
          <div>View your list: </div>
          <Link
            to={
              res.type === ENRICHMENT_TYPE.CONTACT ||
              res.type === ENRICHMENT_TYPE.EMAIL
                ? `/lists/researcher/${res.listId}`
                : `/lists/organisation/${res.listId}`
            }
          >
            {res.listName}
          </Link>
        </LinkContainer>
      )}
    </SummaryContainer>
  );
};

export default SummaryStep;
